import React, { useEffect, useState } from "react";
import { Download, Smartphone, FileText, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * <DownloadPlayer />
 *
 * One button → dialog with two ways to "download" the Mirrorloops Signage player:
 *   1. Install the PWA (Add to Home Screen / Install app) — works on Chrome,
 *      Edge, Safari iOS/macOS, Android tablets, Smart TVs that support PWAs.
 *   2. Download the branded setup PDF (works everywhere — print + stick on the
 *      wall next to the screen).
 *
 * Props:
 *   - pdfUrl: string. Path to the setup-guide PDF (defaults to the public
 *             /api/signage/setup-guide.pdf endpoint).
 *   - className: passes through to the trigger button.
 *   - variant: "primary" | "ghost" | "compact" — controls the trigger style.
 *   - testid: used as the trigger button's data-testid (default "download-player-btn").
 *   - label: trigger button text (default "Download Player").
 */
export default function DownloadPlayer({
  pdfUrl,
  className = "",
  variant = "primary",
  testid = "download-player-btn",
  label = "Download Player",
}) {
  const [open, setOpen] = useState(false);
  const [installEvent, setInstallEvent] = useState(null);
  const [installed, setInstalled] = useState(false);

  // Resolve PDF URL once
  const resolvedPdfUrl =
    pdfUrl || `${process.env.REACT_APP_BACKEND_URL}/api/signage/setup-guide.pdf`;

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setInstallEvent(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const triggerInstall = async () => {
    if (!installEvent) {
      // Safari / iOS — no programmatic prompt available.
      toast.message(
        "On iPhone/iPad: Share → Add to Home Screen. On Android Chrome: ⋮ → Install app."
      );
      return;
    }
    installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === "accepted") {
      toast.success("Mirrorloops Player installed");
      setInstalled(true);
    } else {
      toast.message("Install cancelled");
    }
    setInstallEvent(null);
  };

  const triggerVariant =
    variant === "ghost"
      ? "border border-white/15 text-white/80 hover:border-white"
      : variant === "compact"
      ? "border border-white/15 text-white/80 hover:border-white text-xs h-8"
      : "bg-white text-black font-semibold hover:bg-white/90";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid={testid}
          className={`inline-flex items-center gap-2 px-4 py-2.5 transition rounded-none ${triggerVariant} ${className}`}
        >
          <Download size={14} /> {label}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="bg-[#0d0c0b] border-white/10 text-white max-w-md p-0 overflow-hidden"
        data-testid="download-player-dialog"
      >
        <div className="p-7 border-b border-white/10">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#E8D6A3] mb-2">
            Mirrorloops · signage player
          </div>
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold text-3xl tracking-tight leading-none">
              Download the player.
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-white/60 mt-3">
            The Mirrorloops player runs in any modern browser — pick the install
            path that fits your device. Both options below work on the same
            screen.
          </p>
        </div>

        {/* Option A — Install PWA */}
        <div className="p-7 border-b border-white/10">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-[#D4B878]/10 border border-[#D4B878]/20 text-[#E8D6A3]">
              <Smartphone size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-base">Install as an app</div>
              <p className="text-xs text-white/50 mt-1 leading-relaxed">
                Adds a fullscreen Mirrorloops icon to your tablet, Smart TV, or
                desktop home-screen. No app store. No drivers.
              </p>
              <Button
                onClick={triggerInstall}
                disabled={installed}
                data-testid="download-player-install-pwa"
                className="mt-3 bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-none px-4 h-9 text-sm font-semibold"
              >
                {installed ? (
                  <>
                    <Check size={14} className="mr-2" /> Installed
                  </>
                ) : installEvent ? (
                  "Install Mirrorloops Player"
                ) : (
                  "Show install instructions"
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Option B — Setup PDF */}
        <div className="p-7">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-[#00E5FF]/10 border border-[#00E5FF]/20 text-[#00E5FF]">
              <FileText size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-base">Setup-guide PDF</div>
              <p className="text-xs text-white/50 mt-1 leading-relaxed">
                A printable, branded 1-pager with the play URL, QR code, and
                setup steps for Smart TV, Fire TV, Mac mini, Raspberry Pi, and
                Android tablets.
              </p>
              <a
                href={resolvedPdfUrl}
                download
                target="_blank"
                rel="noreferrer"
                data-testid="download-player-pdf"
                className="mt-3 inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white/80 hover:text-white px-4 h-9 text-sm font-semibold transition"
              >
                <Download size={14} /> Download PDF
              </a>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
